import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import {
    AccountActions,
    AccountSelectors,
    ModulesSelectors,
    DefaultModalConfig,
    ModalsService,
    SosLoaderService,
    LocalizationSelectors,
} from '@serviceos-ng/core';
import { Observable, Subscription } from 'rxjs';
import { filter, finalize, take, tap } from 'rxjs/operators';

@Component({
    selector: 'serviceos-web-addresses-form',
    templateUrl: './addresses-form.component.html',
    styleUrls: ['./addresses-form.component.scss'],
})
export class AddressesFormComponent implements OnInit {
    @Select(AccountSelectors.addresses)
    public addresses!: Observable<
        | Array<{
              address_line_one: string;
              address_line_two: string;
              city: string;
              default: boolean;
              id: number;
              note: string;
              postcode: string;
              sort: number;
              address_formatted?: string
          }>
        | null
        | []
    >;

    @Select(
        ModulesSelectors.getComponentFromModule('customer', 'address_form')
    )
    addressForm$!: Observable<any>;

    @Select(
        ModulesSelectors.getComponentFromModule(
            'account',
            'account_settings_screen.contact_details.addresses'
        )
    )
    addressesConfig$!: Observable<{
        title: string;
        empty_message: string;
        delete_button_title: string;
        add_button_title: string;
        add_form: {
            title: string;
            address_line_one_input_label: string;
            address_line_one_input_error: string;
            address_line_two_input_label: string;
            address_line_two_input_error: string;
            city_input_label: string;
            city_input_error: string;
            postcode_label: string;
            postcode_error: string;
            confirm_button_title: string;
        };
    } | null>;

    public subscriptions: Subscription[] = [];
    public userAddressForm: FormGroup;
    public userAddressFormExpanded = true;
    public showLoader = false;
    public formErrorMessage = '';
    public confirmDeleteModalConfig: any = null;
    public documentDirection = '';

    constructor(
        private _fb: FormBuilder,
        private _store: Store,
        private _modalsService: ModalsService
    ) {
        this.userAddressForm = this._fb.group({
            addressLineOne: [
                '',
                [Validators.required, Validators.minLength(1)],
            ],
            addressLineTwo: [
                '',
                [],
            ],
            postcode: ['', [Validators.required, Validators.minLength(1)]],
            city: ['', []],
        });
    }

    ngOnInit(): void {

        this._store.select(LocalizationSelectors.getCurrentDirection()).subscribe(
            (currentDirection) => {
                if (currentDirection) {
                    this.documentDirection = currentDirection;
                }
            }
        )

        this.confirmDeleteModalConfig = this._store.selectSnapshot(ModulesSelectors.getComponentFromModule('account', 'confirm_delete_modal'));

        this.subscriptions.push(
            this.addresses
                .pipe(
                    tap((addresses) => {
                        this.userAddressFormExpanded =
                            addresses && addresses.length ? false : true;

                        // // Place default address first
                        // if (addresses && addresses.length && addresses.length !== 1) {

                        // 	addresses = addresses.slice().sort((currentAddress, nextAddress) => {
                        // 		if (currentAddress.default) return 1;
                        // 		else return -1;
                        // 	});
                        // }
                    })
                )
                .subscribe()
        );
    }

    /**
     * Combine address properties in single string format
     * @param address
     * @returns
     */
    public formatAddress(address: any): string {
        const cityString: string = address.city ? `, ${address.city}` : '';
        const addressLineTwoString: string = address.address_line_two ? `, ${address.address_line_two}` : '';

        return `${address.address_line_one}${addressLineTwoString}, ${address.postcode}${cityString}`;
    }

    /**
     * Address form submit handler
     */
    public async formSubmit() {
        if (this.userAddressForm.valid) {
            this.showLoader = true;

            const payload: any = {
                address_line_one: this.userAddressForm.value.addressLineOne,
                address_line_two: this.userAddressForm.value.addressLineTwo,
                postcode: this.userAddressForm.value.postcode,
                city: this.userAddressForm.value.city,
            };

            this._store
                .dispatch(new AccountActions.CreateAddress(payload))
                .pipe(
                    finalize(() => {
                        this.showLoader = false;
                    })
                )
                .subscribe(
                    (res) => {
                        this.formErrorMessage = '';

                        this.userAddressForm = this._fb.group({
                            addressLineOne: [
                                '',
                                [Validators.required, Validators.minLength(1)],
                            ],
                            addressLineTwo: [
                                '',
                                [],
                            ],
                            postcode: [
                                '',
                                [Validators.required, Validators.minLength(1)],
                            ],
                            city: [
                                '',
                                [],
                            ],
                        });
                    },
                    (error) => {
                        this.formErrorMessage = error?.error[0]?.message;
                    }
                );
        } else {
            const formControlsNames: Array<string> = Object.keys(
                this.userAddressForm.controls
            );

            // Manually trigger reactive form validation
            for (
                let formControlNameIndex = 0;
                formControlNameIndex < formControlsNames.length;
                formControlNameIndex++
            ) {
                const formControlName: string =
                    formControlsNames[formControlNameIndex];

                this.userAddressForm.controls[formControlName].markAsTouched();
                this.userAddressForm.controls[formControlName].markAsDirty();
            }
        }
    }

    /**
     * Delete address handler
     * @param addressId
     */
    public deleteAddress(addressId: number): void {
        const dialogRef = this._modalsService.open(
            new DefaultModalConfig(
                '', 
                this.confirmDeleteModalConfig.title,
                [
                    {
                        title: this.confirmDeleteModalConfig.cancel_button_title,
                        type: 'close',
                    },
                    {
                        title: this.confirmDeleteModalConfig.confirm_button_title,
                        type: 'confirm',
                    }
                ],
                'warning'
            )
        );

        dialogRef.close$.pipe(take(1)).subscribe((res) => {
            if (res === 'confirm') {
                this.showLoader = true;

                this._store
                    .dispatch(new AccountActions.DeleteAddress({ id: addressId }))
                    .pipe(
                        finalize(() => {
                            this.showLoader = false;
                        })
                    )
                    .subscribe((res) => {
                        this.formErrorMessage = '';
                    },
                    (error) => {
                        this.formErrorMessage = error?.error[0]?.message;
                    });
            }
        });
    }

    /**
     * Mark address as default
     * @param address
     */
    public setAsDefault(address: any): void {
        this.showLoader = true;

        const payload: any = {
            ...address,
            default: true
        };

        this._store
            .dispatch(new AccountActions.MarkAddressAsDefault(payload))
            .pipe(
                finalize(() => {
                    this.showLoader = false;
                })
            )
            .subscribe(
                (res) => {
                    this.formErrorMessage = '';
                },
                (error) => {
                    this.formErrorMessage = error?.error[0]?.message;
                }
            );
    }

    ngOnDestroy(): void {
        this.subscriptions.map((el) => el.unsubscribe());
    }

    get addressLineOne() {
        return this.userAddressForm.get('addressLineOne');
    }
    get addressLineTwo() {
        return this.userAddressForm.get('addressLineTwo');
    }

    get postcode() {
        return this.userAddressForm.get('postcode');
    }

    get city() {
        return this.userAddressForm.get('city');
    }
}
